import { RightOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Col, Flex, Form, Modal, Row, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { acceptRecruiterTerms, declineRecruiterTerms } from '@/api/recruiter';
import FormItem from '@/components/form/FormItem';
import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { buildContractAgreementProvider } from '@/components/recruiter/ContractHistoryModal';
import RecruiterTermsFeeSection from '@/components/recruiter/RecruiterTermsFeeSection';
import { PRODUCT_TOUR_URL } from '@/constants/common';
import { DASHBOARD } from '@/constants/routes';
import { useClientCompany } from '@/hooks/queries/useClientCompany';
import { useRecruiterCompanyAgreement } from '@/hooks/queries/useRecruiterCompanyAgreement';
import { useApiFeedback } from '@/hooks/useApiFeedback';
import { useRefreshSessionUser } from '@/hooks/useRefreshSessionUser';
import { useUrlState } from '@/hooks/useUrlState';
import { MODAL_WIDTH_LG } from '@/theme/buildAntdTheme';
import Loader from '@/UI/Loader';
import { buildRecruiterTermsFormSchema } from '@/utils/validation/formSchemas/miscSchemas';

import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { User } from '@/types/user';

type RecruiterOnboardProps = {
  user: User;
  isOnboard: boolean;
};

const RecruiterOnboard = ({ user }: RecruiterOnboardProps) => {
  const { t } = useTranslation();
  const { showErrorFrom } = useApiFeedback();
  const { history } = useUrlState();
  const refreshSessionUser = useRefreshSessionUser();
  const [form] = Form.useForm();
  const formSchema = useMemo(() => buildRecruiterTermsFormSchema(t), [t]);
  const [unagreedAgreementIds, setUnagreedAgreementIds] = useState<string[]>(
    user.unagreed_agreement_ids ?? [],
  );
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const getVideoUrl = () => {
    return PRODUCT_TOUR_URL;
  };

  const firstAgreementId = unagreedAgreementIds.find((item) => !!item);
  const { data: recruiterCompanyAgreement, isLoading: agreementLoading } =
    useRecruiterCompanyAgreement(firstAgreementId, {
      enabled: !!firstAgreementId,
    });
  const { data: invitingCompany, isLoading: companyLoading } = useClientCompany(
    recruiterCompanyAgreement?.company,
    { enabled: !!recruiterCompanyAgreement?.company },
  );
  const pageLoad = agreementLoading || companyLoading;

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'onboard',
        title: t('RECRUITERS_INVITE_TITLE', {
          company: invitingCompany?.name,
        }),
        fields: [
          {
            key: 'onboard-leadin',
            type: 'custom',
            render: () => (
              <Row>
                <Col>
                  <Flex vertical gap={4}>
                    <Typography.Text>
                      {t('RECRUITERS_INVITE_LEADIN', {
                        company: invitingCompany?.name,
                      })}
                    </Typography.Text>
                    <Typography.Text>
                      <Trans
                        i18nKey="RECRUITERS_INVITE_TERMS_INSTRUCTIONS_VIDEO"
                        components={{
                          a: (
                            <Typography.Link
                              onClick={() => setVideoModalOpen(true)}
                            >
                              video
                            </Typography.Link>
                          ),
                        }}
                      />
                    </Typography.Text>
                  </Flex>
                </Col>
              </Row>
            ),
          },
        ],
      },
      {
        key: 'partnership',
        title: t('RECRUITERS_INVITE_PARTNERSHIP_TITLE'),
        fields: [
          {
            key: 'partnership-items',
            type: 'custom',
            render: () => (
              <Flex vertical gap={4}>
                {[
                  { title: t('RECRUITERS_INVITE_PARTNERSHIP_ITEM_1') },
                  {
                    title: t('RECRUITERS_INVITE_PARTNERSHIP_ITEM_2', {
                      company: invitingCompany?.name,
                    }),
                  },
                  { title: t('RECRUITERS_INVITE_PARTNERSHIP_ITEM_3') },
                ].map((item) => (
                  <Flex key={item.title} align="flex-start">
                    <RightOutlined />
                    <Typography.Text>{item.title}</Typography.Text>
                  </Flex>
                ))}
              </Flex>
            ),
          },
        ],
      },
      {
        key: 'fee-details',
        title: t('RECRUITER_FEE_INFORMATION'),
        hidden: !recruiterCompanyAgreement,
        fields: [
          {
            key: 'fee-details-agency',
            type: 'custom',
            hidden: !recruiterCompanyAgreement?.agency_company?.name,
            render: () => (
              <Alert
                type="info"
                showIcon={false}
                title={t('PROJECT_FOR_CLIENT', {
                  agencyCompany:
                    recruiterCompanyAgreement?.agency_company?.name ?? '',
                  client: invitingCompany?.name ?? '',
                })}
              />
            ),
          },
          {
            key: 'fee-details-content',
            type: 'custom',
            render: () =>
              recruiterCompanyAgreement ? (
                <RecruiterTermsFeeSection
                  agreementProvider={buildContractAgreementProvider(
                    recruiterCompanyAgreement,
                  )}
                />
              ) : null,
          },
        ],
      },
      {
        key: 'terms',
        fields: [
          {
            key: 'terms-content',
            type: 'custom',
            render: () => (
              <Flex vertical gap="middle">
                <Row>
                  <Col xs={24}>
                    <Typography.Text>
                      {t('RECRUITERS_INVITE_TERMS_TITLE', {
                        company: invitingCompany?.name,
                      })}
                    </Typography.Text>
                    <FormItem name="terms" valuePropName="checked">
                      <Checkbox value={false}>
                        {t('RECRUITERS_INVITE_TERMS_LABEL')}
                      </Checkbox>
                    </FormItem>
                  </Col>
                </Row>
                <Alert
                  type="info"
                  showIcon={false}
                  title={
                    <Trans
                      i18nKey="RECRUITERS_INVITE_TERMS_IMPORTANT"
                      values={{ company: invitingCompany?.name }}
                      components={{ b: <strong /> }}
                    />
                  }
                />
              </Flex>
            ),
          },
        ],
      },
    ],
    [t, invitingCompany, recruiterCompanyAgreement],
  );

  const onFinish = async (values: { terms?: boolean }) => {
    if (!firstAgreementId) return;
    try {
      if (values.terms) await acceptRecruiterTerms(firstAgreementId, values);
      else await declineRecruiterTerms(firstAgreementId, values);
      const remainingAgreementIds = unagreedAgreementIds.filter(
        (id) => id !== firstAgreementId,
      );
      if (remainingAgreementIds.length <= 0) {
        await refreshSessionUser();
        history.push(DASHBOARD);
        return;
      }
      setUnagreedAgreementIds(remainingAgreementIds);
      form.resetFields();
    } catch (error) {
      showErrorFrom(error);
    }
  };

  return pageLoad ? (
    <Loader spinning={pageLoad} />
  ) : (
    <SchemaForm
      scrollToFirstError={true}
      name="client-onboard"
      onFinish={(values: { terms?: boolean }) => void onFinish(values)}
      form={form}
      formSchema={formSchema}
      sections={sections}
      footer={{
        dangerActions: [
          {
            label: t('DECLINE_CONTRACT'),
            variant: 'dashed',
            danger: true,
            skipSchemaValidate: true,
            onClick: () => {
              form.setFieldsValue({ terms: false });
              void onFinish({ terms: false });
            },
          },
        ],
        actions: [
          {
            label: t('PROCEED_TITLE'),
            type: 'primary',
            onClick: () => {
              form.setFieldsValue({ terms: true });
              form.submit();
            },
          },
        ],
      }}
    >
      <Modal
        centered
        open={videoModalOpen}
        onOk={() => setVideoModalOpen(false)}
        onCancel={() => setVideoModalOpen(false)}
        footer={null}
        width={MODAL_WIDTH_LG}
        className="video"
        destroyOnHidden
        styles={{
          body: {
            height: '70dvh',
          },
        }}
      >
        <iframe
          width="100%"
          height="100%"
          style={{ border: 0 }}
          src={getVideoUrl()}
          title={t('HIREPORT_INSTRUCTION_VIDEO')}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </Modal>
    </SchemaForm>
  );
};

export default RecruiterOnboard;
